import React from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Divider, Col, Row, Input, Form, Space, Button, DatePicker, Tooltip, Select, InputNumber, Flex } from 'antd';
import type { UploadFile } from 'antd/es/upload';
import { RollbackOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import { HookAPI } from 'antd/es/modal/useModal';
import { NotificationInstance } from 'antd/es/notification/interface';

import CargoCarrier from '@components/cargo-carrier/cargo-carrier';
import UploadDocument from '@components/upload-document/upload-document';

import Trackpoints from './trackpoints';

import { useAppSelector } from '@store/hooks';

import { serverFetch } from '@flp/server';

import { IUserSession } from '@entities/user-session';
import { ITruck } from '@entities/truck';
import { IWaypointHeader } from '@entities/waypoint-header';

import { TruckStatus, enumLabel } from '@enums/truck-status';
import { Permission, hasPermission } from '@enums/permission';
import { Currency, enumSign as currencySign } from '@core/enums/currency';
import { DocumentReferenceType } from '@enums/document-reference-type';
import { DocumentType } from '@enums/document-type';
import { CargoCarrierType } from '@src/core/enums/cargo-carrier-type';

dayjs.extend(utc);

interface ITruckWeb {
    truck: ITruck;
    canManageTruck: boolean;
    modal: HookAPI;
    headers: Array<IWaypointHeader>;
    loading: boolean;
    api: NotificationInstance;
    onFinish: () => void;
    onChange: (e: ITruck) => void;
    onRevertOnway: () => void;
    onClose: () => void;
}

const TruckWeb = (props: ITruckWeb) => {
    const { TextArea } = Input;

    const { truck, canManageTruck, modal, headers, loading, api, onChange, onFinish, onRevertOnway, onClose } = props;

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    return (
        <Row>
            <Col span={10}>
                <Form colon={false} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} onFinish={onFinish}>
                    <Form.Item
                        initialValue={truck.number}
                        required={true}
                        label='Номер'
                        name='number'
                        rules={[{ required: true, message: 'Укажите номер машины' }]}
                    >
                        <Input
                            disabled={!canManageTruck}
                            autoFocus
                            onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                                onChange({ ...truck, number: data.target.value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        required={true}
                        label='Маршрут'
                        name='waypointHeaderId'
                        initialValue={truck.waypointHeaderId}
                        rules={[{ required: true, message: 'Укажите маршрут' }]}
                    >
                        <Select
                            disabled={truck.status == TruckStatus.Finished || truck.status == TruckStatus.Cancelled}
                            onChange={(value) => {
                                if (truck.status != TruckStatus.New) {
                                    modal.confirm({
                                        title: `Маршурт будет перестроить, продолжить?`,
                                        icon: <QuestionCircleOutlined />,
                                        okType: 'primary',
                                        okText: 'ОК',
                                        cancelText: 'Отмена',
                                        onOk: () => onChange({ ...truck, waypointHeaderId: value }),
                                    });
                                } else {
                                    onChange({ ...truck, waypointHeaderId: value });
                                }
                            }}
                            options={headers.map((h) => {
                                return { value: h.id, label: h.name };
                            })}
                        />
                    </Form.Item>

                    <Form.Item initialValue={truck.length} label='Длина' name='length'>
                        <InputNumber
                            disabled={!canManageTruck}
                            precision={1}
                            decimalSeparator=','
                            onChange={(value: number | null) => {
                                onChange({ ...truck, length: value });
                            }}
                        />
                    </Form.Item>

                    {truck.id && (
                        <Form.Item label='Статус'>
                            <Space.Compact style={{ width: '100%' }}>
                                <Input disabled={true} defaultValue={enumLabel(truck.status)} />
                                {hasPermission(userSession.permissions, Permission.FullAccess) && truck.status === TruckStatus.Finished && (
                                    <Tooltip title='Вернуть статус "В Пути"'>
                                        <Button
                                            type='primary'
                                            icon={<RollbackOutlined />}
                                            onClick={() => {
                                                modal.confirm({
                                                    title: `Изменить статус машины на "В Пути"?`,
                                                    okType: 'primary',
                                                    icon: <QuestionCircleOutlined />,
                                                    okText: 'ОК',
                                                    cancelText: 'Отмена',
                                                    onOk: () => {
                                                        onRevertOnway();
                                                    },
                                                });
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Space.Compact>
                        </Form.Item>
                    )}

                    {truck.id && (
                        <>
                            <Form.Item
                                label='Дата отгрузки'
                                name='departureOn'
                                initialValue={
                                    truck.departureOn && dayjs(truck.departureOn).utc(true).set('hour', 0).set('minute', 0).set('second', 0)
                                }
                            >
                                <DatePicker
                                    disabled={!canManageTruck}
                                    format={'DD.MM.YYYY'}
                                    onChange={(data) => {
                                        onChange({
                                            ...truck,
                                            departureOn: dayjs(data).utc(true).set('hour', 0).set('minute', 0).set('second', 0),
                                        });
                                    }}
                                />
                            </Form.Item>
                            <Form.Item label='Брутто' name='grossWeight' initialValue={truck.grossWeight}>
                                <InputNumber
                                    decimalSeparator=','
                                    min={0}
                                    disabled={!canManageTruck}
                                    stringMode
                                    addonAfter='кг'
                                    onChange={(value: number | null) => {
                                        onChange({ ...truck, grossWeight: value });
                                    }}
                                />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item initialValue={truck.trackingUrl} label='Трекинг URL' name='trackingUrl'>
                        <Input
                            disabled={!canManageTruck}
                            onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                                if (!truck) return;

                                onChange({ ...truck, trackingUrl: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label='Комментарий' name='comment' initialValue={truck.comment}>
                        <TextArea
                            disabled={!canManageTruck && !hasPermission(userSession.permissions, Permission.SetCommentTruck)}
                            rows={4}
                            onChange={(data) => {
                                onChange({ ...truck, comment: data.target.value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item label='Перевозчик'>
                        <CargoCarrier
                            defaultCargoCarrier={truck.cargoCarrier}
                            type={CargoCarrierType.Truck}
                            canManage={canManageTruck}
                            api={api}
                            onSelect={(value: string | undefined) => {
                                onChange({ ...truck, cargoCarrier: value });
                            }}
                            serverFetch={serverFetch}
                        />
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 8, span: 16, xs: 16 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            {
                                <Button type='text' onClick={() => onClose()}>
                                    Закрыть
                                </Button>
                            }

                            {(canManageTruck || hasPermission(userSession.permissions, Permission.SetCommentTruck)) && (
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    Сохранить
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>

                {truck.id && (
                    <>
                        <Divider titlePlacement='left' style={{ marginTop: 40 }}>
                            Документы для клиентов
                        </Divider>
                        <Form.Item
                            wrapperCol={{ span: 24 }}
                            style={{ textAlign: 'left', marginTop: 10, border: '1px solid #d9d9d9', borderRadius: 5, padding: 5 }}
                        >
                            <UploadDocument
                                documentType={DocumentType.Other}
                                referenceId={truck.id}
                                referenceType={DocumentReferenceType.Truck}
                                files={truck.documents}
                                api={api}
                                onChange={(fileList: Array<UploadFile>) => {
                                    if (truck) {
                                        onChange({ ...truck, documents: fileList });
                                    }
                                }}
                                serverFetch={serverFetch}
                            />
                        </Form.Item>

                        <Divider titlePlacement='left' style={{ marginTop: 40, backgroundColor: 'var(--main-red)' }}>
                            Документы для внутреннего использования
                        </Divider>
                        <Form.Item
                            wrapperCol={{ span: 24 }}
                            style={{ textAlign: 'left', marginTop: 10, border: '1px solid #d9d9d9', borderRadius: 5, padding: 5 }}
                        >
                            <UploadDocument
                                documentType={DocumentType.InternalUseOnly}
                                referenceId={truck.id}
                                referenceType={DocumentReferenceType.Truck}
                                files={truck.internalDocuments}
                                api={api}
                                onChange={(fileList: Array<UploadFile>) => {
                                    if (truck) {
                                        onChange({ ...truck, internalDocuments: fileList });
                                    }
                                }}
                                serverFetch={serverFetch}
                            />
                        </Form.Item>
                    </>
                )}
            </Col>

            <Col offset={2} span={12}>
                {truck.id && truck.status !== TruckStatus.New && (
                    <>
                        <Divider style={{ marginTop: 0 }} titlePlacement='left'>
                            Трекинг
                        </Divider>
                        {/*   <Trackpoints truck={truck} viewOnly={!canManageTruck || truck.status === TruckStatus.Finished} /> */}
                    </>
                )}
            </Col>
        </Row>
    );
};

export default TruckWeb;
